import { boxLineTimes } from '../../utils/boxLinePoints';
// frontend/src/components/charts/FuturesChartUser.js
import { formatExitReason } from '../../utils/exitReasonLabel';
import {
  boxLineColor,
  boxLineStroke,
  boxLineActive,
} from '../../utils/boxLineStyle';
import { remainingAfterExit } from '../../utils/autoTradingExits';
import { useState, useEffect, useRef, useMemo } from 'react';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import StockTools from 'highcharts/modules/stock-tools';
import PriceIndicator from 'highcharts/modules/price-indicator';
import AnnotationsAdvanced from 'highcharts/modules/annotations-advanced';
import FullScreen from 'highcharts/modules/full-screen';
import Accessibility from 'highcharts/modules/accessibility';
import axios from 'axios';
import FuturesInfo from '../common/FuturesInfo';
import MemoPopup from '../common/MemoPopup';
import AutoTradingStatsPanel from '../pages/AutoTradingStatsPanel';
import CalendarDisplay from '../calendars/CalendarDisplay';

// 모듈 초기화 (내보내기가 default인지 아닌지 조건 처리)
if (typeof StockTools === 'function') {
  StockTools(Highcharts);
}
if (typeof AnnotationsAdvanced === 'function') {
  AnnotationsAdvanced(Highcharts);
}
if (typeof PriceIndicator === 'function') {
  PriceIndicator(Highcharts);
}
if (typeof FullScreen === 'function') {
  FullScreen(Highcharts);
}
if (typeof Accessibility === 'function') {
  Accessibility(Highcharts);
}

// 평일, 주말 판단 함수
function isNowWeekend(d) {
  const date = d instanceof Date ? d : new Date(d);
  const dow = date.getDay(); // 0:Sun, 1:Mon, …, 6:Sat

  return dow === 0 || dow === 6;
}

// 초기 로딩 데이터 범위 설정 - 평일: 과거 24시간, 주말: 과거 48시간
const INITIAL_HOURS_RANGE_FETCH = isNowWeekend(Date.now()) ? 48 : 24;

// 라운드피겨 박스라인 설정
const ROUND_FIGURE_BOX_LINES_VALUE = [
  10000, 11000, 12000, 13000, 14000, 15000, 16000, 17000, 18000, 19000, 20000,
  21000, 22000, 23000, 24000, 25000, 26000, 27000, 28000, 29000, 30000, 31000,
  32000, 33000, 34000, 35000, 36000, 37000, 38000, 39000, 40000,
];

// 청산 사유 한글 매핑
const EXIT_REASON_LABELS = {
  stop_loss: '손절',
  break_even: '본청',
  take_profit_30: '30p분할청산',
  take_profit_45: '45p분할청산',
  power_50: '강도50%청산',
  power_zero: '강도 0 청산 ',
  market_end: '장마감 청산',
  power_80: '강도80%청산',
  power_85: '강도85%청산',
  power_90: '강도90%청산',
  premarket_profit_over_100p: '프리장100p익절',
  price_over_25p_during_auto_trading_blocked: '25p이탈 진입차단',
};

//자동거래 라벨 괴거 n일치 까지 보이도록 설정
const OFFSET_SESSION_DAYS = isNowWeekend() ? 5 : 3;

// 서머타임 여부 확인: true (CDT, Daylight Time(서머타임)), false (CST, Standard Time)
const IS_DST = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Chicago',
  timeZoneName: 'short',
})
  .formatToParts(new Date())
  .some((p) => p.value === 'CDT');

export default function FuturesChartUser() {
  /* ----------------------------------- */
  /* --------- { 상태 관리 파트 } --------- */
  /* ----------------------------------- */
  const [chartOptions, setChartOptions] = useState({});
  const [latestClose, setLatestClose] = useState(0);
  const [boxLines, setBoxLines] = useState([]);
  const [isBoxLines, setIsBoxLines] = useState([]);
  const [loadingPrev, setLoadingPrev] = useState(false);
  const [tradeLabels, setTradeLabels] = useState([]); // annotation.labels

  const chartRef = useRef(null);
  const lastTimestampRef = useRef(0); // 마지막으로 받아온 ohlcp의 가장 최근의 timestamp 저장
  const firstTimestampRef = useRef(0); // 마지막으로 받아온 ohlcp의 가장 맨 앞의 timestamp 저장
  const earliestTsRef = useRef(null); // 과거 차트 데이터 로드 기능을 위한 Ref. 초기 로딩이 끝난 직후 가장 앞쪽 데이터의 시각을 저장
  const isBoxLineRef = useRef(isBoxLines); // 최신 is_boxline 목록
  const loadCountRef = useRef(0); // 과거 데이터 로드 최대 횟수 제한을 위한 Ref
  const tradeLabelsPollId = useRef(null); // trade labels 폴링 타이머
  const tradeLabelsBusyRef = useRef(false); // 중복 호출 방지 플래그

  // interval 관리용 refs
  const latestId = useRef();
  const boxId = useRef();
  const isBoxlineId = useRef();
  const pollingTimerRef = useRef(null); // 실시간 setInterval ID

  useEffect(() => {
    isBoxLineRef.current = isBoxLines;
  }, [isBoxLines]); // is_boxline 목록 최신화 -> ref 동기화 (stale closure 방지)

  /* ------------------------------------------- */
  /* ------------- { 유틸/헬퍼 } ------------- */
  /* ------------------------------------------- */

  // 서버가 tz-aware(+09:00) ISO를 기대하므로 KST 고정 ISO 생성
  function getKSTIsoString(date) {
    const formatted = new Intl.DateTimeFormat('sv-SE', {
      timeZone: 'Asia/Seoul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(date); // "2025-04-17 07:00:00"
    return formatted.replace(' ', 'T') + '+09:00';
  }

  const fmt2 = (n) =>
    n == null
      ? ''
      : Number(n).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

  const mapExitReason = (reason, event) =>
    formatExitReason(reason, event, EXIT_REASON_LABELS);

  function getSessionStartMsKST(offsetSessions = 0, now = new Date()) {
    const nowKST = new Date(
      now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }),
    );
    const start = new Date(nowKST);

    if (IS_DST) {
      // 서머타임인 경우
      if (nowKST.getHours() >= 7) {
        start.setHours(7, 0, 0, 0); // 오늘 07:00
      } else {
        start.setDate(start.getDate() - 1);
        start.setHours(7, 0, 0, 0); // 어제 07:00
      }
    } else {
      // 비 서머타임인 경우
      if (nowKST.getHours() >= 8) {
        start.setHours(8, 0, 0, 0); // 오늘 08:00
      } else {
        start.setDate(start.getDate() - 1);
        start.setHours(8, 0, 0, 0); // 어제 08:00
      }
    }

    // 세션 오프셋 적용: -1 => 어제 세션 시작, -2 => 그저께 세션 시작 …
    return start.getTime() + offsetSessions * 24 * 60 * 60 * 1000;
  }

  /* ------------------------------------------- */
  /* ---------- { 자동거래 라벨 표시 파트 } ---------- */
  /* ------------------------------------------- */

  // history-range API로 구간 내 전략을 받아 라벨 배열 생성
  async function fetchTradeLabelsForRange(startMs, endMs) {
    try {
      const startMsSafe = Number.isFinite(startMs)
        ? startMs
        : getSessionStartMsKST(-OFFSET_SESSION_DAYS - loadCountRef.current);
      const endMsSafe = Number.isFinite(endMs)
        ? endMs
        : lastTimestampRef.current
          ? new Date(lastTimestampRef.current).getTime()
          : Date.now();

      const startIso = getKSTIsoString(new Date(startMsSafe));
      const endIso = getKSTIsoString(new Date(endMsSafe));

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/auto-trading/strategies/history-range`,
        {
          params: {
            start: startIso,
            end: endIso,
            include_open_spanning: true, // 구간을 걸치는 열린 포지션 포함
          },
        },
      );
      const strategies = res.data ?? [];

      // events_history에서 enter/exit 추출 → 라벨 포인트 생성
      const labels = [];
      for (const s of strategies) {
        const directionLong = s.direction === 'long' || s.direction === true;
        const dirLabel = directionLong ? '매수' : '매도';
        const evts = Array.isArray(s.events_history)
          ? [...s.events_history]
          : [];
        evts.sort((a, b) => new Date(a.t) - new Date(b.t));

        let open = null;
        for (const e of evts) {
          if (e.type === 'enter') {
            open = {
              t: e.t,
              price: Number(e.price),
              open_qty: Number(e.qty ?? 1),
            };
            // 진입 라벨
            labels.push({
              point: {
                xAxis: 0,
                yAxis: 0,
                x: new Date(open.t).getTime(),
                y: open.price,
              },
              text: `진입(${dirLabel})\n${fmt2(open.price)}`,
              backgroundColor: directionLong
                ? 'rgba(230, 56, 56, 0.341)'
                : 'rgba(37, 100, 235, 0.337)',
              borderColor: directionLong ? '#dc2626' : '#2563eb',
              borderWidth: 1,
              shape: 'callout',
              style: {
                fontSize: '10px',
                fontWeight: '600',
                color: 'black',
              },
              // 약간 위/아래로 오프셋
              // y: directionLong ? -10 : 10,
              verticalAlign: 'bottom',
              includeInDataExport: false,
            });
          } else if (e.type === 'exit' && open) {
            const exitPrice =
              e.price != null
                ? Number(e.price)
                : s.exit_price != null
                  ? Number(s.exit_price)
                  : null;
            const profit =
              typeof e.profit === 'number'
                ? e.profit
                : exitPrice != null
                  ? (exitPrice - open.price) * (directionLong ? 1 : -1)
                  : null;
            const profitTxt =
              profit == null ? '' : `\nP/L ${profit.toFixed(2)}p`;
            const reasonTxt = e.reason || s.exit_reason || '';
            labels.push({
              point: {
                xAxis: 0,
                yAxis: 0,
                x: new Date(e.t).getTime(),
                y: exitPrice ?? open.price,
              },
              text: `${mapExitReason(reasonTxt, e)}\n${fmt2(exitPrice ?? '')}`,
              backgroundColor:
                profit != null
                  ? profit >= 0
                    ? 'rgba(16,185,129,0.14)'
                    : 'rgba(239,68,68,0.14)'
                  : 'rgba(107,114,128,0.14)',
              borderColor:
                profit != null
                  ? profit >= 0
                    ? '#10b981'
                    : '#ef4444'
                  : '#6b7280',
              borderWidth: 1,
              shape: 'callout',
              style: { fontSize: '10px', fontWeight: '600', color: 'black' },
              y: profit != null ? (profit >= 0 ? -10 : 10) : 0,
              verticalAlign: 'bottom',
              includeInDataExport: false,
            });
            const remainingQty = remainingAfterExit(open, e, e.qty);
            open =
              remainingQty > 0 ? { ...open, open_qty: remainingQty } : null;
          }
        }

        // 아직 열린 포지션(enter만 있고 exit 없음) — “LIVE” 표기
        // if (open) {
        //   labels.push({
        //     point: {
        //       xAxis: 0,
        //       yAxis: 0,
        //       x: new Date(open.t).getTime(),
        //       y: open.price,
        //     },
        //     text: `진입(${dirLabel})\n${fmt2(open.price)}\nLIVE`,
        //     backgroundColor: 'rgba(234,179,8,0.16)',
        //     borderColor: '#eab308',
        //     borderWidth: 1,
        //     shape: 'callout',
        //     style: { fontSize: '10px', fontWeight: '700' },
        //     y: -10,
        //   });
        // }
      }

      setTradeLabels(labels);
    } catch (e) {
      console.error('자동거래 라벨 조회 실패:', e);
      setTradeLabels([]);
    }
  }

  /* ------------------------------------------- */
  /* ------------- { 함수 정의 파트 } ------------- */
  /* ------------------------------------------- */

  function startIntervals() {
    latestId.current = setInterval(fetchLatest, 1_000);
    boxId.current = setInterval(fetchBoxLines, 10_000);
    isBoxlineId.current = setInterval(fetchAllIsBoxlines, 10_000);
  }

  async function fetchCatchUp() {
    if (!lastTimestampRef.current) return;

    const gapMs = Date.now() - Date.parse(lastTimestampRef.current);
    // 30 분(1,800,000 ms) 이상 비어 있으면 배치 호출
    if (gapMs < 1800_000) return;

    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/data/ls_nasdaq_ohlcp/range`,
        {
          params: { start: lastTimestampRef.current },
        },
      );

      const priceSeries = chartRef.current.chart.series[0];
      const newData = data.map((d) => [
        Date.parse(d.timestamp_korean),
        +d.open,
        +d.high,
        +d.low,
        +d.close,
      ]);
      priceSeries.setData(newData, false); // 한 번에 넣기
      chartRef.current.chart.redraw(false);

      // 가장 마지막 timestamp 저장
      if (data.length) lastTimestampRef.current = data.at(-1).timestamp_korean;
    } catch (e) {
      console.error('catch-up 실패:', e);
    }
  }

  function clearAllIntervals() {
    [latestId, boxId, isBoxlineId].forEach((r) => clearInterval(r.current));
  }

  // page visibility, 인터벌 관리 useEffect
  useEffect(() => {
    startIntervals(); // 마운트 시 폴링 인터벌 한 번만 시작

    // 숨겨졌다 돌아올 때는 잔여 데이터 보충만
    const onVisible = () => {
      if (!document.hidden) {
        fetchCatchUp();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearAllIntervals(); // 언마운트 시만 인터벌 해제
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []); // 최초 1회만

  // 매초 새 데이터 폴링: /data/ls_nasdaq_ohlcp/latest
  const fetchLatest = async () => {
    if (!lastTimestampRef.current || !chartRef.current?.chart) return;
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/data/ls_nasdaq_ohlcp/latest`,
        {
          params: { since: lastTimestampRef.current },
        },
      );
      const arr = Array.isArray(data) ? data : [data];
      if (!arr.length) return;
      const chart = chartRef.current.chart;
      const priceSeries = chart.series[0];
      arr.forEach((item) => {
        const x = new Date(item.timestamp_korean).getTime();
        const y = parseFloat(item.close);
        const open = parseFloat(item.open);
        const high = parseFloat(item.high);
        const low = parseFloat(item.low);
        const close = parseFloat(item.close);
        const last = priceSeries.data.at(-1);
        if (!last || x > last.x)
          priceSeries.addPoint(
            [x, open, high, low, close],
            false,
            priceSeries.data.length > 36000,
          );

        setLatestClose(y);
        lastTimestampRef.current = item.timestamp_korean;
      });
      chart.redraw(true);
    } catch (e) {
      console.error('latest fetch 오류:', e);
    }
  };

  // boxLines 데이터 가져오기
  const fetchBoxLines = async () => {
    await axios
      .get(`${process.env.REACT_APP_API_URL}/boxlines/get_box_lines`, {
        params: {
          days: isNowWeekend()
            ? 5 + loadCountRef.current
            : 3 + loadCountRef.current, // 과거 3일치까지 보이도록 설정
        },
      })
      .then((res) => {
        setBoxLines(res.data);
      })
      .catch((err) => {
        console.error('박스 라인 데이터 fetch 에러:', err);
      });
  };

  const fetchAllIsBoxlines = async () => {
    await axios
      .get(
        `${process.env.REACT_APP_API_URL}/boxlines/get_all_is_box_line_range`,
        {
          params: {
            days: 2 + loadCountRef.current,
          },
        },
      )
      .then((res) => {
        setIsBoxLines(res.data);
      })
      .catch((err) => {
        console.error('fetchAllIsBoxlines fetch 에러:', err);
      });
  };

  /* --------------------------------------------------- */
  /* ---------------- plotLines 계산 관련 ---------------- */
  /* --------------------------------------------------- */

  // 1) most_line 파트
  const calMostPlotLines = () =>
    boxLines
      .filter((line) => line.most_line && !isNaN(line.close_box))
      .map((line, index) => {
        // 1) 최소 타임스탬프(ms)
        const minTsMs = Math.min(...boxLineTimes(line));

        // 2) KST 기준의 '연-월-일'만 떼어 날짜 차이 계산
        const toKSTDateOnly = (ms) => {
          const kst = new Date(
            new Date(ms).toLocaleString('en-US', { timeZone: 'Asia/Seoul' }),
          );
          // 자정(00:00:00)으로 내림
          return new Date(
            Date.UTC(kst.getFullYear(), kst.getMonth(), kst.getDate()),
          );
        };

        const todayKST = toKSTDateOnly(Date.now());
        const targetKST = toKSTDateOnly(minTsMs);

        const MS_PER_DAY = 24 * 60 * 60 * 1000; // 86,400,000ms
        const daysDelta = Math.round((todayKST - targetKST) / MS_PER_DAY); // 항상 캘린더 일수 차이

        return {
          id: `most-${index}`,
          value: +line.close_box,
          color: boxLineColor(line),
          width: boxLineActive(line) ? 2 : 1,
          dashStyle: boxLineActive(line) ? 'Solid' : 'Dash',
          label: {
            text: `${daysDelta}일전 - ${line.close_box}`,
            align: 'left',
          },
        };
      });

  // 3) is_boxline 파트
  const calBoxPlotLines = () =>
    isBoxLines
      .filter((line) => line.is_boxline && !isNaN(line.close_box))
      .map((line, index) => {
        // 1) 최소 타임스탬프(ms)
        const minTsMs = Math.min(...boxLineTimes(line));

        // 2) KST 기준의 '연-월-일'만 떼어 날짜 차이 계산
        const toKSTDateOnly = (ms) => {
          const kst = new Date(
            new Date(ms).toLocaleString('en-US', { timeZone: 'Asia/Seoul' }),
          );
          // 자정(00:00:00)으로 내림
          return new Date(
            Date.UTC(kst.getFullYear(), kst.getMonth(), kst.getDate()),
          );
        };

        const todayKST = toKSTDateOnly(Date.now());
        const targetKST = toKSTDateOnly(minTsMs);

        const MS_PER_DAY = 24 * 60 * 60 * 1000; // 86,400,000ms
        const daysDelta = Math.round((todayKST - targetKST) / MS_PER_DAY); // 항상 캘린더 일수 차이

        return {
          id: `box-${index}`,
          value: +line.close_box,
          color: boxLineColor(line),
          width: boxLineActive(line) ? 2 : 1,
          dashStyle: boxLineActive(line) ? 'Solid' : 'Dash',
          label: {
            text: `${daysDelta}일전 - ${line.close_box}`,
            align: 'left',
          },
        };
      });

  // 4) 라운드 피겨 박스라인 파트
  const calRoundFigureBoxLines = () =>
    ROUND_FIGURE_BOX_LINES_VALUE.map((item) => {
      return {
        id: `round-${item}`,
        value: +item,
        color: 'black',
        width: 2,
        dashStyle: 'solid',
        label: {
          text: `라운드피겨 (${item})`,
          align: 'left',
        },
      };
    });

  /* plotLines에 대한 useMemo 파트 */
  const allPlotLines = useMemo(() => {
    return [
      // ...calMostPlotLines(),
      ...calBoxPlotLines(),
      ...calRoundFigureBoxLines(),
    ];
  }, [boxLines, isBoxLines, ROUND_FIGURE_BOX_LINES_VALUE]);

  /* ---------------------------------------------------- */
  /* ----------- { 과거 차트 데이터 로드 기능 파트 } ----------- */
  /* ---------------------------------------------------- */

  // 과거 데이터를 불러와 차트 앞쪽에 prepend
  const loadPrevData = async () => {
    if (
      !earliestTsRef.current ||
      loadingPrev
      // || loadCountRef.current > 3
    )
      return;
    stopPolling();
    setLoadingPrev(true);

    // 최초 범위: 직전 24 h
    let endDate = new Date(earliestTsRef.current - 1_000);
    let startDate = new Date(earliestTsRef.current - 24 * 3600 * 1_000);

    while (true) {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/data/ls_nasdaq_ohlcp/range`,
          {
            params: {
              start: startDate.toISOString(),
              end: endDate.toISOString(),
            },
          },
        );

        if (data.length) {
          const newPoints = data
            .sort(
              (a, b) =>
                new Date(a.timestamp_korean) - new Date(b.timestamp_korean),
            )
            .map((item) => [
              new Date(item.timestamp_korean).getTime(),
              +item.open,
              +item.high,
              +item.low,
              +item.close,
            ]);

          const chart = chartRef.current.chart;
          const priceSeries = chart.series[0];

          priceSeries.setData(
            [...newPoints, ...priceSeries.options.data],
            false,
          );

          chart.xAxis[0].setExtremes(
            newPoints[0][0], // 새로 추가된 맨 앞 timestamp
            chart.xAxis[0].max, // 기존 max 그대로
            false,
            false,
          );

          chart.redraw();

          earliestTsRef.current = newPoints[0][0];

          await fetchTradeLabelsForRange(
            earliestTsRef.current,
            new Date(lastTimestampRef.current).getTime(),
          );

          break;
        } else {
          // 응답이 200인데 배열이 빈 경우 처리
          throw { response: { status: 404 } };
        }
      } catch (err) {
        //데이터가 완전히 없는 날(예: 주말) -> 24 h 추가 확대
        if (err.response?.status === 404) {
          // 더 뒤로 24 h 확장
          startDate = new Date(startDate - 24 * 3600 * 1_000);
          // 안전장치: 7번 이상 시도 시 중단
          if (endDate - startDate > 7 * 24 * 3600 * 1_000) {
            alert('더 이전 구간의 데이터가 없습니다.');
            break;
          }
          // 다시 while 루프 앞으로
          continue;
        } else {
          console.error(err);
          alert('과거 데이터 로딩 실패');
          break;
        }
      }
    }

    setLoadingPrev(false);
    startPolling();
    loadCountRef.current += 1;
  };

  const startPolling = () => {
    if (pollingTimerRef.current) return; // 이미 돌고 있으면 무시
    pollingTimerRef.current = setInterval(fetchLatest, 5_000); // 5초마다
  };

  const stopPolling = () => {
    if (!pollingTimerRef.current) return;
    clearInterval(pollingTimerRef.current);
    pollingTimerRef.current = null;
  };

  /* ----------------------------------------------- */
  /* -------------- { useEffect 파트 } -------------- */
  /* ------------------------------------------------*/

  // plotLines에 대한 useEffect 파트
  useEffect(() => {
    if (!chartRef.current?.chart) return;
    const yAxis = chartRef.current.chart.yAxis[0];
    if (!yAxis) return;

    yAxis.update({ plotLines: allPlotLines }, false);
    yAxis.chart.redraw(false);
  }, [allPlotLines]);

  // 1) 초기 데이터 로딩: DB의 ls_nasdaq_ohlcp 데이터를 불러와 차트 구성. (timestamp_korean 사용)
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/data/ls_nasdaq_ohlcp/range`, {
        params: {
          start: getKSTIsoString(
            new Date(Date.now() - INITIAL_HOURS_RANGE_FETCH * 60 * 60 * 1000),
          ),
        },
      })
      .then(async (response) => {
        const sorted = response.data.slice().sort((a, b) => {
          return new Date(a.timestamp_korean) - new Date(b.timestamp_korean);
        });

        const priceData = sorted.map((item) => [
          new Date(item.timestamp_korean).getTime(),
          +item.open,
          +item.high,
          +item.low,
          +item.close,
        ]);

        // 마지막, 맨처음 데이터의 timestamp 저장
        if (sorted.length > 0) {
          lastTimestampRef.current = sorted[sorted.length - 1].timestamp_korean;
          firstTimestampRef.current = sorted[0].timestamp_korean;
          earliestTsRef.current = priceData[0][0];
        }

        // 최근 100개 데이터로 xAxis 범위 설정
        // const total = priceData.length;
        // let xAxisOptions = {};
        // if (total > 3600) {
        //   const startTimestamp = priceData[total - 3600][0];
        //   const endTimestamp = priceData[total - 1][0];
        //   xAxisOptions = {
        //     min: startTimestamp,
        //     max: endTimestamp,
        //   };
        // }

        setChartOptions({
          credits: {
            enabled: false,
          },
          chart: {
            height: (9 / 16) * 100 + '%',
            // borderColor: '#EBBA95',
            // borderWidth: 1,
            // borderRadius: 10,
            // spacingRight: 50,
            // redraw 이벤트를 활용하여 plotLines 재렌더링
            zooming: {
              type: 'x',
            },
          },
          palette: {
            colorScheme: 'light',
          },
          time: {
            timezone: 'Asia/Seoul',
          },
          rangeSelector: {
            selected: 2,
            buttons: [
              {
                type: 'minute',
                count: 30,
                text: '30m',
              },
              {
                type: 'hour',
                count: 1,
                text: '1h',
              },
              {
                type: 'hour',
                count: 2,
                text: '2h',
              },
              {
                type: 'hour',
                count: 4,
                text: '4h',
              },
              {
                type: 'hour',
                count: 8,
                text: '8h',
              },
              {
                type: 'day',
                count: 1,
                text: '1D',
              },
              {
                type: 'all',
                text: 'All',
              },
            ],
          },
          tooltip: {
            backgroundColor: '#00ff0000',
            shadow: false,
            split: false,
            shape: 'square',
            headerShape: 'callout',
            shared: true,
            snap: 0,
            positioner: () => ({ x: 10, y: 30 }),
          },
          plotOptions: {
            series: {
              states: {
                hover: {
                  enabled: false,
                },
              },
              lastPrice: {
                enabled: true,
                color: '#00ff0000',
                label: {
                  enabled: true,
                  borderColor: 'grey',
                  borderWidth: 1,
                  backgroundColor: '#00ff0000',
                  format: '{value:.2f}',
                  style: {
                    color: 'black',
                    fontWeight: 'bold',
                    textAlign: 'right',
                  },
                },
              },
            },
          },
          annotations: [],
          responsive: {
            rules: [
              {
                condition: {
                  maxHeight: 800,
                },
              },
            ],
          },
          yAxis: [
            // Main Chart
            {
              title: { text: '가격' },
              height: '100%',
              lineWidth: 2,
              opposite: true,
              labels: {
                align: 'left',
                x: 2,
                format: '{value:.2f}',
              },
              crosshair: {
                label: {
                  enabled: true,
                  format: '{value:.2f}',
                },
                snap: false,
              },
              plotLines: [],
            },
          ],
          xAxis: [
            {
              overscroll: 200 * 1000,
              gridLineWidth: 1,
              tickInterval: 3600 * 1000, // 1시간 = 3600000 밀리초
              minRange: (10 / 60) * 1 * 3600000, // x축 최소 범위: 10분
              align: 'high',
              type: 'datetime',
              // min: xAxisOptions.min,
              // max: xAxisOptions.max,
            },
          ],
          series: [
            {
              type: 'ohlc',
              name: '체결가격',
              data: priceData,
              tooltip: { valueDecimals: 2 },
              lineWidth: 1,
              yAxis: 0,
              color: 'black',
              pointInterval: 1000, // 1초
              dataGrouping: {
                // enabled: false,
                groupPixelWidth: 3,
              },
            },
          ],
          // navigator: {
          //   enabled: false,
          // },
          stockTools: {
            gui: {
              enabled: false,
              // // 기본 설정(원하는 도구들을 제거하면 오류 발생할 수 있으므로 기본 옵션 사용)
              // // 원하는 도구들만 활성화
              // buttons: [
              //   "simpleShapes",
              //   "lines",
              //   "measure",
              //   "toggleAnnotations",
              //   "fullScreen",
              //   "currentPriceIndicator",
              // ],
              // definitions: {
              //   simpleShapes: {
              //     items: ["rectangle", "circle"],
              //   },
              //   lines: {
              //     items: ["horizontalLine"],
              //   },
              //   measure: {
              //     items: ["measureY"],
              //   },
              //   currentPriceIndicator: {
              //     enabled: true,
              //   },
              // },
            },
          },
        });

        startPolling(); // 초기 데이터가 준비된 뒤 폴링 시작

        // 차트 구간에 맞춰 자동거래 라벨 최초 조회
        if (earliestTsRef.current && lastTimestampRef.current) {
          await fetchTradeLabelsForRange(
            getSessionStartMsKST(-OFFSET_SESSION_DAYS - loadCountRef.current),
            new Date(lastTimestampRef.current).getTime(),
          );
        }
      })
      .catch((error) => {
        console.error('LS NASDAQ OHLCP 데이터 로딩 에러:', error);
      });

    return () => stopPolling(); // 언마운트 시 정리
  }, []);

  // 2) boxLines, tradeLabels 업데이트
  useEffect(() => {
    if (!chartRef.current?.chart) return;
    const chart = chartRef.current.chart;

    // boxLines → annotation 배열 한 방에 생성
    const boxAnnotations = boxLines
      .filter(
        (lineItem) =>
          boxLineTimes(lineItem).length >= 2 && !isNaN(lineItem.close_box),
      )
      .map((lineItem, i) => {
        // 봉우리 객체를 시간순으로 정렬한 뒤 x 좌표 추출
        const sortedTimes = boxLineTimes(lineItem);

        // 정렬된 밀리초 배열로 포인트 객체 생성
        const points = sortedTimes.map((x) => ({
          xAxis: 0,
          yAxis: 0,
          x,
          y: lineItem.close_box,
        }));

        return {
          id: `box-${i}`, // 중요: 고유 id -> oneToOne 매칭
          draggable: '',
          type: 'crookedLine', // 루트-레벨
          controlPointOptions: { visible: false },
          typeOptions: {
            points,
            line: boxLineStroke(
              lineItem,
              lineItem.is_top ? 'red' : 'blue',
              'Dash',
            ),
          },
        };
      });

    // 자동거래 라벨(한 개의 annotation 그룹으로 묶음)
    const tradeLabelsAnnotation =
      tradeLabels.length > 0
        ? [
            {
              id: 'trade-labels',
              draggable: 'y',
              labels: tradeLabels,
              // 공통 labelOptions(선택)
              labelOptions: {
                allowOverlap: true, // 겹침 허용(필요 시)
                crop: false,
                overflow: 'justify',
              },
              zIndex: 7,
            },
          ]
        : [];

    // chart.update — oneToOne = true → id가 없는 예전 annotation 은 자동 제거
    chart.update(
      { annotations: [...boxAnnotations, ...tradeLabelsAnnotation] }, // 새 배열 통째로
      false, // redraw 나중에
      true, // oneToOne
    );
    chart.redraw(true);
  }, [boxLines, tradeLabels]);

  // 3) tradeLabels 5초 폴링: 당일 세션 시작~현재(또는 마지막 캔들) 구간 재조회
  useEffect(() => {
    const poll = async () => {
      if (tradeLabelsBusyRef.current) return;
      tradeLabelsBusyRef.current = true;
      try {
        // 차트가 이미 로딩된 경우, 마지막 캔들 시각을 우선 사용
        const endMs = lastTimestampRef.current
          ? new Date(lastTimestampRef.current).getTime()
          : (chartRef.current?.chart?.xAxis?.[0]?.max ?? Date.now());
        const startMs = getSessionStartMsKST(
          -OFFSET_SESSION_DAYS - loadCountRef.current,
        );
        await fetchTradeLabelsForRange(startMs, endMs);
      } catch (e) {
        // 콘솔만 남기고 무시 (다음 폴링에서 재시도)
        console.error('tradeLabels 폴링 에러:', e);
      } finally {
        tradeLabelsBusyRef.current = false;
      }
    };

    // 즉시 1회 실행 + 5초 주기
    poll();
    tradeLabelsPollId.current = setInterval(poll, 5_000);
    return () => {
      if (tradeLabelsPollId.current) {
        clearInterval(tradeLabelsPollId.current);
        tradeLabelsPollId.current = null;
      }
    };
  }, []);

  return (
    <div>
      {/* 차트와 버튼들 영역 */}
      <div className='flex flex-wrap justify-center items-end pt-4 pl-2 '>
        {/* 왼쪽: 차트 영역 */}
        <div className='flex-1 min-w-[30rem]'>
          <div className='flex items-center gap-4'>
            <FuturesInfo latestClose={latestClose} />
            <MemoPopup />
            <div className='text-center'>
              <button
                onClick={loadPrevData}
                disabled={
                  loadingPrev || !chartOptions.series
                  // || loadCountRef.current > 3
                }
                className='inline-flex items-center justify-center rounded-md
                         px-3 py-2 text-white bg-sky-600
                         hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500
                         disabled:opacity-60 disabled:cursor-not-allowed'
              >
                {loadingPrev ? '로딩 중…' : '더 불러오기'}
              </button>
            </div>
          </div>
          {chartOptions.series && (
            <HighchartsReact
              highcharts={Highcharts}
              constructorType='stockChart'
              options={chartOptions}
              ref={chartRef}
            />
          )}
        </div>
        {/* 오른쪽: 거래 통계 영역 */}
        <div className='flex flex-col items-center my-auto pr-3 gap-3'>
          {/* <CalendarDisplay /> */}
          <AutoTradingStatsPanel latestClose={latestClose} />
        </div>
      </div>
    </div>
  );
}
